import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Brand";
import { NAV, SITE } from "../lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${
        scrolled || open ? "bg-navy/85 backdrop-blur-xl border-white/10 py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="MHM SUPER ADBLUE home">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "text-white" : "text-white/70 hover:text-white"}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-brand" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={`tel:+91${SITE.headOffice}`}
            data-testid="nav-call-button"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-brand-dark hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" /> {SITE.headOfficeDisplay}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="px-5 pt-4 pb-6 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.04 }}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-3.5 font-display text-3xl font-bold uppercase ${isActive ? "text-brand" : "text-white"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <a href={`tel:+91${SITE.headOffice}`} className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 font-semibold text-white">
                <Phone className="h-4 w-4" /> Call {SITE.headOfficeDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
